import React from "react";
// Customizable Area Start
import {
  Checkbox,
  TextField,
  Typography,
  Box,
  FormControl,
  MenuItem,
  Select,
  Button,
  Avatar,
  Divider,
  Dialog,
  IconButton,
  FormHelperText,
  DialogContent,
  styled,
} from "@mui/material";
import "@mui/styles";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import EditCareerHightlightsController, {
  Props,
} from "./EditCareerHighlightsController.web";
import { configJSON } from "./CustomisableUserProfilesController";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

const CustomDialog = styled(Dialog)({
  "& .MuiPaper-root": {
    borderRadius: "20px",
    backgroundColor: "#fff",
    padding: "10px",
  },
});
// Customizable Area End

export default class EditCareerHighlights extends EditCareerHightlightsController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }

  render() {
    // Customizable Area Start
    const currentYear = new Date().getFullYear();
    const yearOptions: number[] = Array.from(
      { length: currentYear - 1970 + 1 },
      (_, i) => 1970 + i
    );
    const { image, imagePreview } = this.state;
    let avatarSrc = configJSON.imageUpload;
    let avatarSize = { width: "50%", height: "50%" };

    if (imagePreview) {
      avatarSrc =
        typeof imagePreview === "string"
          ? imagePreview
          : URL.createObjectURL(imagePreview);
      avatarSize = { width: "100%", height: "100%" };
    } else if (image) {
      avatarSrc = typeof image === "string" ? image : configJSON.imageUpload;
      avatarSize = { width: "100%", height: "100%" };
    }
    return (
      <>
        <CustomDialog
          fullWidth
          maxWidth="md"
          data-test-id="handleCareerModal"
          open={this.props.open}
          onClose={this.props.onClose}
        >
          <DialogContent>
            <Box sx={{ ...webStyle.modalStyle }}>
              <Box sx={{ ...webStyle.modalTitleContainer }}>
                <Typography variant="h6" sx={{ ...webStyle.modalTitle }}>
                  Add Career Highlights
                </Typography>
                <IconButton
                  data-test-id="modalClose"
                  onClick={this.props.onClose}
                  sx={{ ...webStyle.closeIcon }}
                >
                  <CloseRoundedIcon />
                </IconButton>
              </Box>
              <Divider />
              <Box>
                <Box sx={{ ...webStyle.uploadImgBox }}>
                  <Box sx={{ ...webStyle.uploadImgInnerBox }}>
                    <input
                      type="file"
                      accept="image/*"
                      data-test-id="upload-image-box"
                      onChange={this.handleFileChange}
                      style={{ display: "none" }}
                      id="upload-image"
                    />
                    <label htmlFor="upload-image">
                      <Box
                        width={60}
                        height={60}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="50%"
                        sx={{
                          cursor: "pointer",
                          overflow: "hidden",
                          background: "#CBD5E1",
                        }}
                      >
                        <Avatar src={avatarSrc} sx={avatarSize} />
                      </Box>
                    </label>
                    <Typography sx={{ ...webStyle.uploadTitle }}>
                      Upload Image <span style={{ color: "#ff0000" }}> *</span>
                    </Typography>
                    {this.state.errors.image && (
                      <FormHelperText style={{ color: "#d32f2f" }}>
                        {this.state.errors.image}
                      </FormHelperText>
                    )}
                  </Box>
                </Box>
                <Box sx={{ ...webStyle.projectBox }}>
                  <Box style={{ width: "100%" }}>
                    <Typography sx={{ ...webStyle.fieldsTitle }}>
                      Project Name <span style={{ color: "#ff0000" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter your project name"
                      variant="outlined"
                      value={this.state.projectName}
                      name="projectName"
                      sx={{ ...webStyle.input }}
                      onChange={this.handleChange}
                      error={!!this.state.errors.projectName}
                      helperText={this.state.errors.projectName}
                      data-test-id="project-name-field"
                      inputProps={{ maxLength: 250 }}
                    />
                  </Box>
                  <Box style={{ width: "100%" }}>
                    <Typography sx={{ ...webStyle.fieldsTitle }}>
                      Role <span style={{ color: "#ff0000" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter your role"
                      variant="outlined"
                      value={this.state.role}
                      name="role"
                      sx={{ ...webStyle.input }}
                      onChange={this.handleChange}
                      error={!!this.state.errors.role}
                      helperText={this.state.errors.role}
                      data-test-id="role-field"
                      inputProps={{ maxLength: 250 }}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{ ...webStyle.projectBox }}
                  data-test-id="start-date-box"
                >
                  <Box style={{ width: "100%" }}>
                    <Typography sx={{ ...webStyle.fieldsTitle }}>
                      Start Date <span style={{ color: "#ff0000" }}>*</span>
                    </Typography>
                    <FormControl fullWidth sx={{ ...webStyle.input }}>
                      <Select
                        name="startMonth"
                        data-test-id="start-month-select"
                        onChange={this.handleStartMonthChange}
                        value={this.state.startMonth}
                        IconComponent={KeyboardArrowDownRoundedIcon}
                        defaultValue=""
                        displayEmpty
                        MenuProps={{
                          sx: {
                            "& .MuiPaper-root": {
                              minWidth: "100%",
                              width: "auto !important",
                            },
                          },
                          PaperProps: {
                            sx: {
                              minWidth: "unset",
                              maxHeight: 200,
                              overflowY: "auto",
                            },
                          },
                        }}
                      >
                        <MenuItem value="" sx={{ display: "none" }}>
                          {" "}
                          Month{" "}
                        </MenuItem>
                        {configJSON.monthName.map(
                          (month: { key: string; value: string }) => (
                            <MenuItem key={month.key} value={month.value}>
                              {month.value}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                    {this.state.errors.startMonth && (
                      <FormHelperText
                        data-test-id="start-month-error"
                        style={{ color: "#d32f2f" }}
                      >
                        {this.state.errors.startMonth}
                      </FormHelperText>
                    )}
                  </Box>
                  <Box style={{ width: "100%", marginTop: "22px" }}>
                    <FormControl fullWidth sx={{ ...webStyle.input }}>
                      <Select
                        data-test-id="start-year-select"
                        name="startYear"
                        value={this.state.startYear}
                        onChange={this.handleStartYearChange}
                        IconComponent={KeyboardArrowDownRoundedIcon}
                        defaultValue=""
                        displayEmpty
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              overflowY: "auto",
                              minWidth: "unset",
                              maxHeight: 200,
                            },
                          },
                          sx: {
                            "& .MuiPaper-root": {
                              width: "auto !important",
                              minWidth: "100%",
                            },
                          },
                        }}
                      >
                        <MenuItem value="" sx={{ display: "none" }}>
                          {" "}
                          Year{" "}
                        </MenuItem>
                        {yearOptions.map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {this.state.errors.startYear && (
                      <FormHelperText
                        data-test-id="start-year-error"
                        style={{ color: "#d32f2f" }}
                      >
                        {this.state.errors.startYear}
                      </FormHelperText>
                    )}
                  </Box>
                </Box>

                <Box sx={{ ...webStyle.projectBox }}>
                  <Box style={{ width: "100%" }}>
                    <Typography sx={{ ...webStyle.fieldsTitle }}>
                      End Date
                    </Typography>
                    <FormControl fullWidth sx={{ ...webStyle.input }}>
                      <Select
                        data-test-id="end-month-select"
                        name="endMonth"
                        defaultValue=""
                        displayEmpty
                        value={this.state.endMonth}
                        onChange={this.handleEndMonthChange}
                        IconComponent={KeyboardArrowDownRoundedIcon}
                        disabled={this.state.ongoing}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              minWidth: "unset",
                              maxHeight: 200,
                              overflowY: "auto",
                            },
                          },
                          sx: {
                            "& .MuiPaper-root": {
                              minWidth: "100%",
                              width: "auto !important",
                            },
                          },
                        }}
                      >
                        <MenuItem value="" sx={{ display: "none" }}>
                          {" "}
                          Month{" "}
                        </MenuItem>
                        {configJSON.monthName.map(
                          (month: { key: string; value: string }) => (
                            <MenuItem key={month.key} value={month.value}>
                              {month.value}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                    {this.state.errors.endMonth && (
                      <FormHelperText
                        data-test-id="end-month-error"
                        style={{ color: "#d32f2f" }}
                      >
                        {this.state.errors.endMonth}
                      </FormHelperText>
                    )}
                  </Box>
                  <Box style={{ width: "100%", marginTop: "22px" }}>
                    <FormControl fullWidth sx={{ ...webStyle.input }}>
                      <Select
                        data-test-id="end-year-select"
                        name="endYear"
                        value={this.state.endYear}
                        defaultValue=""
                        displayEmpty
                        onChange={this.handleEndYearChange}
                        IconComponent={KeyboardArrowDownRoundedIcon}
                        disabled={this.state.ongoing}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              minWidth: "unset",
                              maxHeight: 200,
                              overflowY: "auto",
                            },
                          },
                          sx: {
                            "& .MuiPaper-root": {
                              minWidth: "100%",
                              width: "auto !important",
                            },
                          },
                        }}
                      >
                        <MenuItem value="" sx={{ display: "none" }}>
                          {" "}
                          Year{" "}
                        </MenuItem>
                        {yearOptions.map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {this.state.errors.endYear && (
                      <FormHelperText
                        data-test-id="end-year-error"
                        style={{ color: "#d32f2f" }}
                      >
                        {this.state.errors.endYear}
                      </FormHelperText>
                    )}
                  </Box>
                </Box>
                <Box sx={{ ...webStyle.checkboxDiv }}>
                  <Checkbox
                    name="checkbox"
                    data-test-id="ongoing-checkbox"
                    icon={<this.CustomUncheckedIcon />}
                    checkedIcon={<this.CustomCheckedIcon />}
                    checked={this.state.ongoing}
                    onChange={this.handleCheckboxChange}
                  />
                  <Typography sx={{ ...webStyle.checkBoxTitle }}>
                    Ongoing
                  </Typography>
                </Box>
                <Box sx={{ ...webStyle.projectBox }}>
                  <Box style={{ width: "100%" }}>
                    <Typography sx={{ ...webStyle.fieldsTitle }}>
                      Location <span style={{ color: "#ff0000" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter your location"
                      variant="outlined"
                      value={this.state.location}
                      onChange={this.handleChange}
                      name="location"
                      sx={{ ...webStyle.input }}
                      error={!!this.state.errors.location}
                      helperText={this.state.errors.location}
                      data-test-id="location-field"
                      inputProps={{ maxLength: 250 }}
                    />
                  </Box>
                  <Box style={{ width: "100%" }}>
                    <Typography sx={{ ...webStyle.fieldsTitle }}>
                      Project Link <span style={{ color: "#ff0000" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter your Project Link"
                      variant="outlined"
                      value={this.state.projectLink}
                      onChange={this.handleChange}
                      name="projectLink"
                      sx={{ ...webStyle.input }}
                      error={!!this.state.errors.projectLink}
                      helperText={this.state.errors.projectLink}
                      data-test-id="project-link-field"
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography sx={{ ...webStyle.fieldsTitle }}>
                    Description<span style={{ color: "#ff0000" }}>*</span>
                    <span> (Max character limit 1000)</span>
                  </Typography>
                  <TextField
  fullWidth
  placeholder="Enter your Description"
  value={this.state.description || ''}
  onChange={this.handleChange}
  error={!!this.state.errors.description}
  helperText={this.state.errors.description}
  name="description"
  multiline
  variant="outlined"
  sx={webStyle.descriptionField}
  FormHelperTextProps={{ 
    sx: { 
      position: 'static',
      margin: '4px 0 0 14px'
    } 
  }}
  data-test-id="description-field"
  inputProps={{ 
    maxLength: 1000
  }}
/>
                </Box>
                <Box sx={{ ...webStyle.aboutModalButtonBox }}>
                  <Button
                    sx={{ ...webStyle.aboutSaveModal }}
                    data-test-id="save-career-btn"
                    onClick={this.handleSave}
                    disabled={this.isSaveDisabled()}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            </Box>
          </DialogContent>
        </CustomDialog>
      </>
      // Customizable Area End
    );
    // Customizable Area End
  }
}

// Customizable Area Start
const webStyle = {
  modalStyle: {
    bgcolor: "#FFFFFF",
  },
  uploadImgBox: {
    display: "flex",
    justifyContent: "center",
    textAlign: "center",
    alignItems: "center",
    alignContent: "center",
    marginTop: "20px",
  },
  uploadImgInnerBox: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: "20px",
  },
  uploadTitle: {
    fontSize: "16px",
    fontWeight: "500",
    lineHeight: "19.5px",
    letterSpacing: "-0.15px",
    color: "#0F172A",
    fontFamily: "Montserrat !important",
    marginTop: "10px",
    "@media (max-width: 550px)": {
      fontSize: "14px",
      lineHeight: "16px",
    },
  },
  fieldsTitle: {
    fontSize: "16px",
    fontWeight: "500",
    lineHeight: "22px",
    fontFamily: "Montserrat !important",
    marginBottom: "5px",
    "@media (max-width: 550px)": {
      fontSize: "14px",
      lineHeight: "18px",
    },
  },
  checkboxDiv: {
    display: "flex",
    alignItems: "center",
    marginBottom: "10px",
  },
  checkBoxTitle: {
    fontSize: "16px",
    fontWeight: "500",
    lineHeight: "22px",
    fontFamily: "Montserrat !important",
  },
  modalTitleContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "20px",
  },
  modalTitle: {
    fontSize: "20px",
    fontWeight: "500",
    lineHeight: "24.38px",
    fontFamily: "Montserrat !important",
    "@media (max-width: 550px)": {
      fontSize: "18px",
      lineHeight: "20px",
    },
  },
  input: {
    fontSize: "16px",
    lineHeight: "24px",
    color: "#64748b !important",
    opacity: "1 !important",
    "& .MuiOutlinedInput-root": {
      borderRadius: "50px",
      borderColor: "#e2e8f0",
      "&:hover": { borderColor: "#cbd5e1" },
      "&.Mui-focused": {
        borderColor: "#673ab7",
        boxShadow: "0 0 0 3px rgba(103, 58, 183, 0.2)",
      },
    },
  },
  descriptionField: {
    width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "32px",
    borderColor: "#e2e8f0",
    "&:hover": { 
      borderColor: "#cbd5e1" 
    },
    "&.Mui-focused": {
      borderColor: "#673ab7",
      boxShadow: "0 0 0 3px rgba(103, 58, 183, 0.2)",
    }
  },
  "& .MuiOutlinedInput-input": {
    height: "100px !important",
    overflowY: "auto",
    padding: "12px 14px",
    fontSize: "16px",
    lineHeight: "24px",
    color: "#64748b",
    "&::-webkit-scrollbar": {
      width: "4px"
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0,0,0,0.2)",
      borderRadius: "4px"
    }
  }
  },
  closeIcon: {
    p: 0,
    "& svg": {
      fontSize: "24px",
      fill: "#000",
    },
  },
  customScrollbar: {
    "&::-webkit-scrollbar": {
      width: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "20px",
    },
    "&:hover::-webkit-scrollbar-thumb": {
      background: "rgba(100, 100, 100, 0.7)",
    },
    "&:hover::-webkit-scrollbar-thumb:hover": {
      background: "rgba(100, 100, 100, 1)",
    },
  },
  projectBox: {
    display: "flex",
    justifyContent: "space-between",
    gap: "15px",
    marginBottom: "20px",
  },
  aboutModalButtonBox: {
    display: "flex",
    justifyContent: "center",
    textAlign: "center",
    marginTop: "30px",
  },
  aboutSaveModal: {
    fontSize: "14px !important",
    fontWeight: 600,
    width: "360px !important",
    color: "#fff !important",
    lineHeight: "24px !important",
    border: "1px solid currentColor",
    borderRadius: "32px",
    padding: "10px 39px !important",
    textTransform: "none",
    cursor: "pointer",
    background: "linear-gradient(90deg, #8077FF 0%, #652784 60%)",
    opacity: 1,
    transition: "opacity 0.3s",
    "&:disabled": {
      opacity: 0.5,
      pointerEvents: "none",
    },
    "@media (max-width: 1280px)": {
      fontSize: "12px",
    },
  },
};
// Customizable Area End
